import envVariables from '../../../consts/envVariables';
import {selectIsAuthorized} from '../../../state/selectors/auth';
import {getState} from '../../../state/store';
import helpers from '../../../utils/helpers';
import iapClientService from '../../iap/iapClientService';
import localhostService from '../../localhostService';
import storageService from '../../storageService';

let instance = null;

const CREDENTIALS_EXPIRATION_OFFSET = 10 * 1000; // 10 seconds

class CloudWatchClient {
    constructor() {
        if (instance) {
            return instance;
        }

        instance = this;
    }

    putLogs = async (logs) => {
        if (localhostService.getShouldSkipIapRequestsOnLocalhost()) return;

        await this.#setCloudWatchConfig();

        if (this.isUserAuthorized) {
            await this.#putLogsAuthorized(logs);
        } else {
            await this.#putLogsUnauthorized(logs);
        }
    };

    #putLogsAuthorized = async (logs) => {
        const {PutLogEventsCommand} = await import('@aws-sdk/client-cloudwatch-logs');

        const command = new PutLogEventsCommand(this.#prepareEvent(logs));

        const data = await this.client.send(command);

        this.nextSequenceToken = data ? data.nextSequenceToken : null;
    };

    #putLogsUnauthorized = async (logs) => {
        await iapClientService.postAnonymousPushLogs({logs}, this.unauthorized.token);
    };

    #prepareEvent = (logs) => {
        const logging = this.authorized.logging;

        const data = {
            logEvents: [...logs],
            logGroupName: logging.logGroupName,
            logStreamName: logging.logStreamName,
            sequenceToken: logging?.uploadSequenceToken,
        };

        if (this.nextSequenceToken) data.sequenceToken = this.nextSequenceToken;

        return data;
    };

    #getCredentials = async () => {
        if (
            !this?.authorized?.credentials?.expirationTs ||
            this?.authorized?.credentials?.expirationTs <= new Date().getTime() + CREDENTIALS_EXPIRATION_OFFSET
        ) {
            this.authorized = await iapClientService.getCredentialsForLogging();
        }
    };

    #getUnauthorizedUserCredentials = async () => {
        const exp = this?.unauthorized?.token ? helpers.parseJwt(this?.unauthorized?.token)?.exp : null;

        if (new Date(exp * 1000).getTime() <= new Date().getTime() + CREDENTIALS_EXPIRATION_OFFSET) {
            const data = await iapClientService.postAnonymousCredentialsForLogging();

            this.unauthorized = {token: data?.token};
        }
    };

    #setCloudWatchConfig = async () => {
        this.isUserAuthorized = selectIsAuthorized(getState());

        if (this.isUserAuthorized) {
            await this.#getCredentials();
            await this.#createCloudWatchClient();
        } else {
            await this.#getUnauthorizedUserCredentials();
        }
    };

    #createCloudWatchClient = async () => {
        const {CloudWatchLogsClient} = await import('@aws-sdk/client-cloudwatch-logs'); // minimize bundle

        this.client = new CloudWatchLogsClient({
            region: envVariables.AWS_REGION,
            credentials: this.authorized.credentials,
            maxAttempts: 0,
        });
    };

    getLogStreamName = () => {
        if (this?.logStreamName) {
            return this.logStreamName;
        }

        this.logStreamName = storageService.getLogStreamNameFromLocalStorage();

        if (this.logStreamName) {
            return this.logStreamName;
        }

        this.logStreamName = helpers.guid();

        storageService.setLogStreamNameToLocalStorage(this.logStreamName);

        return this.logStreamName;
    };
}

export default CloudWatchClient;
